import type { WindLine } from './types';

// Polaroid positioning from Scene2 layout
const POLAROID_LAYOUT = {
  LEFT: 850,
  WIDTH: 478,
  get RIGHT() {
    return this.LEFT + this.WIDTH; // 1328
  },
} as const;

// Star final position in Scene2
const STAR_FINAL_POSITION = 1394;

export const ANIMATION_CONFIG = {
  // Horizontal offset: start from Polaroid's right edge
  HORIZONTAL_START_OFFSET: POLAROID_LAYOUT.RIGHT - STAR_FINAL_POSITION, // -66px
  ARRIVAL_DURATION_FRAMES: 40,

  // Vertical bounce parameters
  BOUNCE_SPEED: 0.15,
  BOUNCE_AMPLITUDE_PX: 8,
  BOUNCE_START_THRESHOLD: 0.99, // Start when 99% arrived

  // Scale zoom parameters
  ZOOM_SPEED: 0.1,
  ZOOM_AMPLITUDE: 0.15, // ±15%

  // Fly-in scale interpolation
  FLY_IN_SCALE: {
    keyframes: [0, 0.5, 1],
    values: [0.5, 1.25, 1], // Small → overshoot → settle
  },

  // Fly-in opacity interpolation
  FLY_IN_OPACITY: {
    keyframes: [0, 0.25, 1],
    values: [0, 1, 1], // Fade in early
  },
} as const;

export const SPRING_CONFIG = {
  damping: 15,
  stiffness: 120,
  mass: 0.75,
} as const;

// SMIL animation constants
export const SMIL_ANIMATION = {
  KEY_TIMES: '0; 0.5; 1',
  REPEAT_COUNT: 'indefinite',
} as const;

// Wind line configurations with 0.25 step rounding
export const WIND_LINES: WindLine[] = [
  {
    d: 'M -25 8 C -10 8, 10 8, 25 8',
    dashArray: 50,
    opacity: 0.75,
    begin: '0s',
    duration: '2s',
    dashOffset: [0, -10, -50],
    dashValues: [0, 35, 0],
  },
  {
    d: 'M -30 18 C -18 18, -8 18, 8 18',
    dashArray: 38,
    opacity: 0.5,
    begin: '0.25s',
    duration: '2.25s',
    dashOffset: [0, -8, -38],
    dashValues: [0, 28, 0],
  },
  {
    d: 'M -30 26.5 C -20 26.5, -10 26.5, 5 26.5',
    dashArray: 35,
    opacity: 0.75,
    begin: '0.5s',
    duration: '2.5s',
    dashOffset: [0, -5, -35],
    dashValues: [0, 25, 0],
  },
  {
    d: 'M -28 35 C -16 35, -5 35, 10 35',
    dashArray: 38,
    opacity: 0.5,
    begin: '0.75s',
    duration: '2s',
    dashOffset: [0, -6, -38],
    dashValues: [0, 28, 0],
  },
  {
    d: 'M -28 43 C -15 43, -2 43, 12 43',
    dashArray: 40,
    opacity: 0.75,
    begin: '1s',
    duration: '2.25s',
    dashOffset: [0, -8, -40],
    dashValues: [0, 30, 0],
  },
  {
    d: 'M -32 50 C -20 50, -8 50, 6 50',
    dashArray: 38,
    opacity: 0.25,
    begin: '1.25s',
    duration: '2.5s',
    dashOffset: [0, -7, -38],
    dashValues: [0, 28, 0],
  },
];

export const STAR_PATH_DATA =
  'M38.178 0C38.976 4.13956e-06 40.1114 0.168768 41.3199 0.89384C42.5299 1.61989 43.7712 2.87881 44.8181 4.99171L48.9624 13.3555L48.9944 13.4095C49.0836 13.5449 49.2704 13.7442 49.5244 13.9364C49.7823 14.1316 50.0126 14.2463 50.1491 14.2851L50.2018 14.2971L50.2062 14.2977L57.7132 15.5453C59.9695 15.9196 61.5012 16.7404 62.5312 17.6849C63.5583 18.6268 64.0535 19.661 64.2862 20.4159C64.5172 21.165 64.7038 22.2895 64.4067 23.6532C64.1089 25.0198 63.3348 26.5815 61.7145 28.2165L55.8559 34.1284C55.7473 34.238 55.5943 34.5168 55.489 34.8955C55.384 35.2731 55.3691 35.5967 55.4068 35.7567L55.4074 35.7605L57.086 43.0687C57.6811 45.6714 57.519 47.6048 57.0094 49.0282C56.4991 50.4538 55.658 51.3176 54.984 51.8107C54.3139 52.301 53.2439 52.8493 51.7374 52.9072C50.327 52.9613 48.5854 52.5842 46.486 51.4068L46.0613 51.1603L39.0254 46.942L39.0229 46.9401C38.8701 46.8477 38.5477 46.7657 38.1485 46.7657C37.7493 46.7657 37.4268 46.8477 37.2741 46.9401L37.2716 46.942L30.2356 51.1603C28.1246 52.4557 26.3043 52.9153 24.8713 52.9153C23.2438 52.9153 22.0781 52.3546 21.3593 51.802C20.6856 51.3059 19.853 50.4358 19.3465 49.0169C18.8372 47.5905 18.6743 45.6574 19.2699 43.068L20.9479 35.7605L20.9491 35.7567C20.9867 35.5971 20.9723 35.2812 20.8676 34.9081C20.7645 34.5407 20.611 34.2506 20.4881 34.1165L14.6414 28.2165C13.0211 26.5814 12.247 25.0198 11.9492 23.6532C11.6521 22.2895 11.8387 21.165 12.0697 20.4159C12.3029 19.6593 12.7985 18.6325 13.8254 17.6949C14.8546 16.7552 16.3851 15.9354 18.6389 15.5459L26.1497 14.2977L26.1541 14.2971C26.2811 14.2768 26.5486 14.1565 26.8453 13.9345C27.1432 13.7116 27.3294 13.4916 27.3804 13.3825L27.3835 13.3756L27.3873 13.3687L31.5378 4.99171C32.5848 2.87879 33.826 1.61988 35.036 0.89384C36.2445 0.168759 37.3799 0 38.178 0ZM21.0438 50.6547C21.1246 50.7436 21.2058 50.8259 21.2859 50.9025L21.1655 50.7833C21.1251 50.7419 21.0843 50.6992 21.0438 50.6547ZM38.178 5.74065C38.1837 5.74065 38.1396 5.74092 38.0431 5.78832C37.9497 5.83424 37.8238 5.91624 37.6743 6.05365C37.3745 6.3292 37.0055 6.80177 36.6336 7.55217L32.4843 15.9248L32.485 15.9254C31.5404 17.8635 29.3829 19.4963 27.2706 19.9242L27.0667 19.9618L19.5629 21.2088C18.7813 21.3475 18.251 21.5556 17.9163 21.757C17.5677 21.9668 17.4861 22.1335 17.4772 22.1616C17.4673 22.1938 17.4404 22.3816 17.6058 22.7556C17.765 23.1155 18.0811 23.5946 18.6452 24.1638L24.5031 30.0751C25.3452 30.9248 25.966 32.0966 26.3165 33.3167C26.667 34.5368 26.7633 35.8594 26.4991 37.0263L26.4984 37.0282L24.8205 44.3364L24.8193 44.3407C24.5814 45.3434 24.5479 46.0796 24.5979 46.5713C24.6229 46.8172 24.6677 46.991 24.7108 47.1026C24.7206 47.1279 24.7299 47.1484 24.7377 47.1647C24.7512 47.1665 24.7669 47.1708 24.786 47.1722C24.8977 47.18 25.0714 47.1715 25.3092 47.1214C25.7834 47.0213 26.465 46.7658 27.3302 46.2307L27.339 46.2257L34.4019 42.0086L34.6089 41.8894C36.776 40.7002 39.8698 40.7368 41.9528 42.008L48.9856 46.2244C49.8693 46.7481 50.5629 47.0062 51.0461 47.1107C51.2884 47.1631 51.4645 47.1743 51.5774 47.1678C51.5957 47.1667 51.6109 47.1624 51.6238 47.1609C51.6298 47.1482 51.6396 47.1339 51.647 47.1151C51.6885 47.0098 51.733 46.8396 51.7581 46.5932C51.808 46.1014 51.7744 45.3577 51.536 44.3389L51.5354 44.3364L49.8575 37.0282L49.8568 37.0244C49.594 35.8473 49.6799 34.5269 50.0275 33.3073C50.3749 32.0881 50.9977 30.9213 51.8572 30.0707L57.7107 24.1638C58.2726 23.5968 58.5895 23.1108 58.7495 22.7462C58.8293 22.5641 58.8663 22.4201 58.8799 22.3172C58.8924 22.2226 58.8829 22.1787 58.8818 22.1735C58.8818 22.1735 58.8779 22.1609 58.863 22.1371C58.8482 22.1135 58.8246 22.0816 58.789 22.043C58.7172 21.9653 58.6038 21.869 58.4358 21.7664C58.0997 21.5612 57.5696 21.3467 56.7924 21.2088L49.2892 19.9618C47.1131 19.6013 44.8459 17.9259 43.8709 15.9254L39.7223 7.55217C39.3504 6.80177 38.9814 6.3292 38.6816 6.05365C38.5321 5.91625 38.4062 5.83425 38.3128 5.78832C38.2162 5.74084 38.1721 5.74065 38.178 5.74065ZM48.9981 46.9715C49.0471 46.9984 49.0963 47.0232 49.1443 47.0486C49.0895 47.0196 49.0336 46.9905 48.9774 46.9595L48.9981 46.9715ZM19.6482 44.544C19.5898 44.9646 19.5547 45.3622 19.5397 45.7376C19.5509 45.4562 19.5742 45.1624 19.6093 44.8557C19.621 44.7534 19.6342 44.6498 19.6488 44.5446C19.6634 44.4394 19.679 44.3328 19.6965 44.2247C19.6789 44.3328 19.6628 44.4388 19.6482 44.544ZM24.0434 44.9379C24.0418 44.9471 24.0411 44.9564 24.0396 44.9655C24.0422 44.9491 24.0444 44.9325 24.0471 44.9159L24.0434 44.9379ZM12.5332 23.2982C12.5598 23.4456 12.5926 23.5964 12.6323 23.7504L12.5771 23.5165C12.5611 23.4429 12.5462 23.3702 12.5332 23.2982ZM63.7236 23.7504L63.7757 23.5315C63.7766 23.5274 63.7766 23.5231 63.7775 23.519C63.7609 23.5952 63.7437 23.6725 63.7236 23.7504ZM27.8119 13.8912C27.8227 13.8772 27.8345 13.8643 27.8445 13.8504L27.8489 13.8442C27.8375 13.8601 27.8243 13.8751 27.8119 13.8912Z';
